/**
 * A reviewer-typed quantity, checked the same way in the resolve dialog and
 * the resolveDiscrepancies mutation. Quantity is pounds and must be above 0.
 */
export type TypedQuantity =
  | { ok: true; value: number }
  | { ok: false; reason: "EMPTY" | "NOT_A_NUMBER" | "NOT_POSITIVE" };

// Thousands groups of exactly three digits, so "1,200" reads as 1200 but a
// stray comma ("12,00") is rejected rather than guessed at.
const COMMA_GROUPED = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/;

export function parseTypedQuantity(raw: string): TypedQuantity {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false, reason: "EMPTY" };
  const digits = COMMA_GROUPED.test(trimmed) ? trimmed.replace(/,/g, "") : trimmed;
  // Number("12abc") is NaN where parseFloat would read 12, so only a full parse passes.
  // ast-grep-ignore: no-unsafe-number-parsing-ts
  const value = Number(digits);
  if (!Number.isFinite(value)) return { ok: false, reason: "NOT_A_NUMBER" };
  if (value <= 0) return { ok: false, reason: "NOT_POSITIVE" };
  return { ok: true, value };
}

/** True when a stored quantity string is a number above 0, so it can be declared correct. */
export function isPositiveQuantity(value: string | null): boolean {
  if (value == null) return false;
  // ast-grep-ignore: no-unsafe-number-parsing-ts
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}
